import { get } from 'lodash';
import { treatErrorAs } from 'api/errors';
import { findResource } from 'loading/finders';
// Processors
import { processGeneratedZones, processCompleteMapLayers, processAvailableLayers } from 'manual/processors';
// Selectors
import { getActiveUserId, getActiveFieldId, getActiveFieldUuid } from 'current/selectors';
import {
  getActiveFieldAvailableLayers,
  getCropIdsForAvailableDataLayers,
  getSelectedLayers,
  getSelectedMapLayer,
  getSelectedMapLayerUuid,
  isMultiLayerZoneGenerationEnabled
} from 'manual/selectors';
// Loaders
import { 
  clusterZonesMutation, 
  pollJobStatus, 
  fetchClusteredZones, 
  completeMapLayersMutation, 
  fetchCompleteMapLayers, 
  fetchGeneratedZones,
  fetchDataProcessingAndAvailableLayers
}
  from './loaders';
// Constants
import { errorLevel } from 'constants/errors';
import { resourceType } from 'constants/loading';
import { parseAvailableLayers } from 'manual/layersParser';
import { ZONE_GENERATION_MODEL } from 'manual/constants';
// Action Creators
import { setAvailableLayersError } from 'manual';

export const findManualGeneratedZones = ({ dispatch, getState, numZones = 5 }) => {
  const state = getState();
  const userId = getActiveUserId(state);
  const fieldId = getActiveFieldId(state);
  const fieldUuid = getActiveFieldUuid(state);
  const eventDate = getSelectedMapLayerUuid(state);
  const isMultiLayerEnabled = isMultiLayerZoneGenerationEnabled(state);
  
  if (isMultiLayerEnabled) {
    const selectedLayers = getSelectedLayers(state);
    const { selectedMapType } = getSelectedMapLayer(state);
    const model = ZONE_GENERATION_MODEL[selectedMapType];
    const selectedLayersIds = selectedLayers.map(({ id }) => id);
    return fetchGeneratedZones({ fieldUuid, numberOfZones: numZones, model, selectedLayersIds })
    .then((generatedZones) => processGeneratedZones({ dispatch, generatedZones, isMultiLayerEnabled }));
  }
  
  return Promise.resolve()
  .then(() => {
    const mutationPromise = clusterZonesMutation({userId, fieldId, numberOfZones: numZones, eventDate});
    const jobStatusPromise = mutationPromise.then(response => {
      const jobId = get(response, 'data.clusterZones.jobId');
      return pollJobStatus({userId, jobId});
    });
    const fetchClusterZonePromise = jobStatusPromise.then(() => {
      return fetchClusteredZones({userId, fieldId, numberOfZones: numZones, eventDate});
    });
    return fetchClusterZonePromise.then((results) => {
      const generatedZones = get(results, 'data.clusteredZones');
      return processGeneratedZones({ dispatch, generatedZones });
    });
  })
  .catch(err => {
    treatErrorAs({ severity: errorLevel.BLOCK, andStopHere: true })(err);
  });
};

export const findCompleteMapLayersJobId = ({dispatch, getState, fieldId, year}) => {
  const state = getState();
  const userId = getActiveUserId(state);
  return Promise.resolve()
  .then(() => {
    return completeMapLayersMutation({fieldId, userId, year})
    .catch(treatErrorAs({ severity: errorLevel.INFO }))
    .then((response) => {
      const { data: {calculateCompleteLayers: { jobId }} } = response;
      return processCompleteMapLayers({dispatch, jobId, fieldId, year})
      .catch(treatErrorAs({ severity: errorLevel.INFO }));
    });
  }).catch(treatErrorAs({ severity: errorLevel.INFO }));
};

export const findCompleteLayers = ({dispatch, jobId, userId, fieldId, year}) => {
  return Promise.resolve()
  .then(() => {
    return pollJobStatus({userId, jobId}).catch(err => {
      treatErrorAs({ severity: errorLevel.BLOCK, andStopHere: true })(err);
    });
  }).then(() => {
    return fetchCompleteMapLayers({userId, fieldId, year}).catch(err => {
      treatErrorAs({ severity: errorLevel.BLOCK, andStopHere: true })(err);
    });
  })
  .then((response) => {
    const { data: {completeLayers: {completeLayers}}} = response;
    return processCompleteMapLayers({dispatch, fieldId, jobId, year, completeLayers});
  });
};

export const findAvailableLayers = ({ dispatch, getState }) => {
  const state = getState();
  const fieldUuid = getActiveFieldUuid(state);
  const cropIds = getCropIdsForAvailableDataLayers(state);
  
  const resourceSpec = {
    type: `${resourceType.LAYERS_FOR_ZONE_GENERATION}_${fieldUuid}`,
    selector: () => getActiveFieldAvailableLayers(getState()),
    fetcher: () => fetchDataProcessingAndAvailableLayers(fieldUuid, cropIds),
    processor: (availableLayers) => processAvailableLayers({ 
      dispatch,
      availableLayers: parseAvailableLayers(availableLayers),
      fieldUuid
    }),
    forceReload: false,
    forceWait: false,
    onError: (error) => {
      dispatch(setAvailableLayersError(fieldUuid));
      treatErrorAs({ severity: errorLevel.WARN })(error);
    }
  };

  return findResource({ dispatch, getState, ...resourceSpec });
};
